import React, { useContext, useEffect, useState } from "react";
import publicAxios from "axios";
import { AuthContext } from "../../contexts/Auth/Auth.Context";
import axios from "../../utils/axios";
import { REACT_APP_SESSION_DATA, REACT_APP_ACCOUNT_DATA, REACT_APP_UPLOADS } from "../../constants";
import { Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

interface FormData {
  email:    string;
  password: string;
}

const fieldCls =
  'w-full px-4 py-3 rounded-xl bg-white border border-gray-200 text-gray-900 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500 transition-all';

export function LoginForm(_props: React.ComponentPropsWithoutRef<"form">) {
  const { dispatch } = useContext(AuthContext);

  const [formData, setFormData]         = useState<FormData>({ email: "", password: "" });
  const [loading, setLoading]           = useState(false);
  const [errorMsg, setErrorMsg]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [summary, setSummary]           = useState<{ totalRecords?: number; speciesTypes?: number; countriesActive?: number } | null>(null);

  useEffect(() => {
    publicAxios
      .get(`${REACT_APP_UPLOADS}/api/v1/seizure-cases/summary`)
      .then((res) => { if (res.status === 200) setSummary(res.data); })
      .catch(() => { /* summary unavailable — stats show placeholders */ });
  }, []);

  const fmt = (n?: number) => (n ? `${n}+` : "—");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMsg("");
  };

  const handleLogin = () => {
    setLoading(true);
    axios
      .post("/users/login", { username: formData.email, password: formData.password })
      .then((res) => {
        if (res.status === 200) {
          localStorage.setItem(REACT_APP_SESSION_DATA, res.data?.token);
          if (res.data?.user) localStorage.setItem(REACT_APP_ACCOUNT_DATA, JSON.stringify(res.data.user));
          dispatch({ type: "SIGN_IN", token: res.data?.token, data: res.data?.user });
        }
      })
      .catch((err: any) => { setErrorMsg(err?.message ?? "Login failed. Please try again."); })
      .finally(() => setLoading(false));
  };

  return (
    <div className="min-h-[calc(100vh-120px)] flex" style={{ background: '#fafaf7' }}>

      {/* ── Left: brand panel ── */}
      <div
        className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between p-12 xl:p-16 relative overflow-hidden"
        style={{ background: '#052e16' }}
      >
        <img
          src="/images/v1/img12.jpg"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ opacity: 0.2 }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#052e16]/80 via-[#052e16]/60 to-transparent pointer-events-none" />

        {/* Logo */}
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3">
            <span
              className="text-2xl font-bold text-white"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              WITS
            </span>
            <span
              className="text-green-400 text-xs tracking-[0.15em] uppercase font-medium mt-1"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              by LAPA Africa
            </span>
          </Link>
        </div>

        {/* Copy */}
        <div className="relative z-10">
          <h2
            className="text-4xl xl:text-5xl font-bold text-white leading-[1.1] mb-5"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Intelligence that<br />
            <span className="text-green-400">protects wildlife</span>
          </h2>
          <p
            className="text-white/55 text-sm leading-relaxed max-w-sm"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Sign in to access real-time incident data, case records, and
            conservation intelligence across Africa.
          </p>

          {/* Stats row */}
          <div className="flex gap-8 mt-10">
            {[
              [fmt(summary?.totalRecords),    'Incidents tracked'],
              [fmt(summary?.speciesTypes),    'Species monitored'],
              [fmt(summary?.countriesActive), 'Countries active'],
            ].map(([v, l]) => (
              <div key={l}>
                <p className="text-green-400 text-2xl font-bold" style={{ fontFamily: 'var(--font-display)' }}>{v}</p>
                <p className="text-white/40 text-xs mt-0.5" style={{ fontFamily: 'var(--font-body)' }}>{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right: form ── */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-12 xl:px-16">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <a href="/" className="flex items-center gap-2 mb-10 lg:hidden">
            <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-display)' }}>WITS</span>
            <span className="text-green-700 text-xs tracking-[0.12em] uppercase font-medium" style={{ fontFamily: 'var(--font-body)' }}>by LAPA Africa</span>
          </a>

          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
            Sign in
          </h1>
          <p className="text-gray-500 text-sm mb-8" style={{ fontFamily: 'var(--font-body)' }}>
            Welcome back. Enter your credentials to continue.
          </p>

          {errorMsg && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" style={{ fontFamily: 'var(--font-body)' }}>
              {errorMsg}
            </div>
          )}

          <form
            onSubmit={(e) => { e.preventDefault(); handleLogin(); }}
            className="flex flex-col gap-5"
          >
            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>
                Username / Email
              </label>
              <input
                id="email"
                name="email"
                type="text"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your username or email"
                required
                className={fieldCls}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-600 mb-1.5" style={{ fontFamily: 'var(--font-body)' }}>
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  className={`${fieldCls} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
                    </svg>
                  ) : (
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="accent-green-600 w-3.5 h-3.5" />
                <span className="text-xs text-gray-500" style={{ fontFamily: 'var(--font-body)' }}>Remember me</span>
              </label>
              <Link
                to="/accounts/forgot-password"
                className="text-xs text-green-700 font-medium hover:underline"
                style={{ fontFamily: 'var(--font-body)' }}
              >
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-full bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-green-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 flex items-center justify-center gap-2"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              {loading && <Loader2 className="animate-spin w-4 h-4" />}
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-8" style={{ fontFamily: 'var(--font-body)' }}>
            Don&apos;t have an account?{' '}
            <Link to="/accounts/register" className="text-green-700 font-semibold hover:underline">
              Create one
            </Link>
          </p>

        </div>
      </div>
    </div>
  );
}
